import { useState, useCallback, useEffect, useRef } from 'react';
import type { Dispatch, SetStateAction } from 'react';

import { useScreenPreferences } from '@/hooks/userPreference';

import { useAbortController } from './useAbortController';
import { useTableSearch } from './table/useTableSearch';


export interface PaginatedListSort {
  key: string;
  direction: 'ASC' | 'DESC';
}

/**
 * Parameters passed to the domain-level `fetchFn` on every fetch.
 */
export interface PaginatedFetchParams<TFilters> {
  page: number;
  pageSize: number;
  searchText: string | undefined;
  sort: PaginatedListSort | undefined;
  filters: TFilters;
}

/**
 * Normalised result that `fetchFn` must return on success.
 */
export interface PaginatedFetchResult<TRow> {
  items: TRow[];
  totalRecords: number;
}

export interface UsePaginatedListConfig<TRow, TFilters> {
  /**
   * Async function that calls the service and returns normalised data.
   * - On success: return `{ items, totalRecords }`.
   * - On error: call `showApiError` (or equivalent), then return `null`.
   *
   * **Must be wrapped in `useCallback`** to avoid infinite re-fetch loops,
   * because it is used as a dependency of the internal fetch effect.
   */
  fetchFn: (
    params: PaginatedFetchParams<TFilters>,
    signal: AbortSignal,
  ) => Promise<PaginatedFetchResult<TRow> | null>;

  /** Initial filter state. */
  initialFilters: TFilters;

  /** Initial sort applied on mount. */
  initialSort?: PaginatedListSort;

  /** Rows per page on mount. @default 10 */
  initialPageSize?: number;

  /**
   * Maps Table column keys to backend field names for server-side sorting.
   * An inline object literal is safe here — the hook stabilises it via a ref.
   */
  orderByMap?: Record<string, string>;

  /**
   * When `true`, deduplicates rows by their `id` field after each fetch.
   * Requires that `TRow` has an `id: number | string` property.
   * @default false
   */
  deduplicate?: boolean;

  /**
   * When `false`, the fetch is skipped without changing the loading state.
   * Useful for permission gates: `enabled: isLoaded && canView`.
   * The fetch is automatically triggered when this switches to `true`.
   * @default true
   */
  enabled?: boolean;

  /**
   * Stable screen code (the same `code` used in `ROUTE_DEFINITIONS`) that opts this list into
   * persisted user preferences for `filters`, `sort`, and `pageSize` via `useScreenPreferences`.
   * When a page hosts more than one independent list under the same route code, append a
   * sub-key to disambiguate (e.g. `'5.1.8.region'` vs `'5.1.8.type'`).
   *
   * Omitting it (the default) keeps this list's state purely local, exactly as before —
   * no network call, no Redux cache entry.
   */
  screenCode?: string;

  /**
   * Called once, right when a persisted preference finishes hydrating and is about to be
   * applied — only if the user hasn't already interacted with the list in the meantime. Domain
   * hooks whose own panel/filter-editing state lives outside `filters` (e.g. a separate
   * `pendingFilters`) use this to stay in sync with what just got restored.
   *
   * Return a value to override the filters that actually get applied (e.g. to recompute a
   * relative date range instead of restoring its frozen persisted value); returning `undefined`
   * applies the hydrated filters as-is.
   */
  onHydrated?: (hydrated: {
    filters: TFilters;
    sort: PaginatedListSort | undefined;
    pageSize: number;
  }) => TFilters | void;
}

/** Shape persisted per screen when `screenCode` is provided. */
interface PersistedListPreferences<TFilters> extends Record<string, unknown> {
  filters: TFilters;
  sort?: PaginatedListSort;
  pageSize?: number;
}

export interface UsePaginatedListResult<TRow, TFilters> {
  rows: TRow[];
  total: number;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  /** Normalises the value and resets page to 1. */
  setPageSize: (size: number) => void;
  loading: boolean;
  onSearch: (text: string) => void;
  onSortChange: (columnKey: string, direction: 'asc' | 'desc') => void;
  /** Current sort state (useful when domain hooks need to pass sort to export functions). */
  sort: PaginatedListSort | undefined;
  filters: TFilters;
  /** Updates filters and automatically resets page to 1 when the value changes. */
  setFilters: Dispatch<SetStateAction<TFilters>>;
  /** Current debounced search text (useful when the domain hook needs to include it in exports). */
  debouncedSearchText: string;
  /** Forces the debounced search to resolve immediately (e.g. on Enter key). */
  flushSearch: () => void;
  /** Manually triggers a re-fetch with the current parameters. */
  refetch: () => void;
}

/**
 * Generic hook that encapsulates all pagination boilerplate for list pages.
 *
 * Handles: pagination state, debounced search, server-side sort, filter state,
 * abort-signal staleness detection, page-overflow guard, optional dedup, and — when a
 * `screenCode` is passed — persistence of `filters`/`sort`/`pageSize` as a per-user screen
 * preference (via `useScreenPreferences`, the single allowed access point for that storage).
 *
 * Domain hooks stay thin: they provide a `fetchFn`, columns, actions, and any
 * domain-specific state (e.g. totals, catalogs, permission checks).
 *
 * @example
 * ```ts
 * const fetchFn = useCallback(async (params, signal) => {
 *   const response = await myService.list({ ...params.filters, ...params }, signal);
 *   if (!response.success || !response.data) {
 *     showApiError(response, translate('domain:messages.loadError'));
 *     return null;
 *   }
 *   return { items: response.data.items, totalRecords: response.data.totalRecords };
 * }, [showApiError, translate]);
 *
 * const { rows, total, page, setPage, loading, onSearch, onSortChange, filters, setFilters } =
 *   usePaginatedList({
 *     fetchFn,
 *     initialFilters: { status: 'all' },
 *     enabled: isLoaded,
 *     screenCode: ROUTE_DEFINITIONS.EMPLEADOS.code, // optional — persists filters/sort/pageSize
 *   });
 * ```
 */
export function usePaginatedList<TRow, TFilters>({
  fetchFn,
  initialFilters,
  initialSort,
  initialPageSize = 10,
  orderByMap,
  deduplicate = false,
  enabled = true,
  screenCode,
  onHydrated,
}: UsePaginatedListConfig<TRow, TFilters>): UsePaginatedListResult<TRow, TFilters> {
  // Called unconditionally (rules of hooks) even when `screenCode` is omitted — passing '' to
  // `useScreenPreferences` disables persistence entirely (no fetch, no cache), making this a
  // no-op for the majority of call sites that haven't opted into a screen code yet.
  const {
    preferences: listPreferences,
    updatePreferences: updateListPreferences,
    isLoaded: listPreferencesLoaded,
  } = useScreenPreferences<PersistedListPreferences<TFilters>>(screenCode ?? '', {
    filters: initialFilters,
    sort: initialSort,
    pageSize: initialPageSize,
  });

  const [page, internalSetPage] = useState(1);
  const [pageSize, internalSetPageSize] = useState(
    () => listPreferences.pageSize ?? initialPageSize,
  );
  // Tracks the pageSize actually chosen by the user (`setPageSize`) or restored from a
  // persisted preference — independent of the transient reset `setFilters` applies to the
  // *displayed* pageSize whenever filters change (see below). Only this ref gets persisted, so
  // picking 50 rows and then tweaking a filter doesn't silently downgrade the saved preference.
  const persistedPageSizeRef = useRef(pageSize);
  const [rows, setRows] = useState<TRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sort, setSort] = useState<PaginatedListSort | undefined>(
    () => listPreferences.sort ?? initialSort,
  );
  const [filters, internalSetFilters] = useState<TFilters>(
    () => listPreferences.filters ?? initialFilters,
  );

  const { createSignal } = useAbortController();
  const { debouncedSearchText, setSearchText, flushSearch } = useTableSearch({
    onDebouncedChange: () => internalSetPage(1),
  });

  // Stabilise orderByMap via a ref so callers can pass an inline object literal
  // without needing useMemo, and without triggering unnecessary effect re-runs.
  const orderByMapRef = useRef<Record<string, string>>(orderByMap ?? {});
  useEffect(() => {
    orderByMapRef.current = orderByMap ?? {};
  }, [orderByMap]);

  // Preferences load asynchronously on a first visit (cache hit resolves synchronously — see
  // `useScreenPreferences`), so the lazy initialisers above may have already committed the
  // fallback defaults by the time real data arrives. This re-syncs the internal state once,
  // the first time `listPreferencesLoaded` flips to `true` for the current `screenCode`.
  const [hasHydratedFromPreferences, setHasHydratedFromPreferences] = useState(
    () => !screenCode || listPreferencesLoaded,
  );

  // Set the first time the caller changes filters/sort/pageSize. Guards the hydration effect
  // below: if the user acts before the persisted preference finishes loading, their change must
  // win — otherwise the late-arriving (stale) preference would clobber what they just set.
  const userInteractedRef = useRef(false);

  // Resets on every `screenCode` change (including a dynamic reassignment, not just the initial
  // mount) so a screen swap doesn't inherit the previous screen's "already hydrated"/"user
  // interacted" state. Deliberately does not read `listPreferencesLoaded` here: within the same
  // commit, `useScreenPreferences`'s own reset of `isLoaded` for the new `screenCode` may not
  // have propagated to this render's closure yet, so trusting it would let a stale `true`
  // through. Setting `false` unconditionally (when a screenCode is active) is safe — the
  // hydration effect below re-derives the correct value once `listPreferencesLoaded` settles.
  useEffect(() => {
    userInteractedRef.current = false;
    setHasHydratedFromPreferences(!screenCode);
  }, [screenCode]);

  useEffect(() => {
    if (!screenCode || hasHydratedFromPreferences) return;
    if (!listPreferencesLoaded) return;
    if (!userInteractedRef.current) {
      const hydratedFilters = listPreferences.filters ?? initialFilters;
      const hydratedSort = listPreferences.sort ?? initialSort;
      const hydratedPageSize = listPreferences.pageSize ?? initialPageSize;
      internalSetFilters(
        onHydrated?.({ filters: hydratedFilters, sort: hydratedSort, pageSize: hydratedPageSize })
          ?? hydratedFilters,
      );
      setSort(hydratedSort);
      internalSetPageSize(hydratedPageSize);
      persistedPageSizeRef.current = hydratedPageSize;
    }
    setHasHydratedFromPreferences(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screenCode, listPreferencesLoaded, hasHydratedFromPreferences]);

  // Persists filters/sort/pageSize once hydration has settled — before that point, `filters`/
  // `sort`/`pageSize` may still hold pre-hydration defaults that would otherwise clobber a
  // just-loaded preference. Persists `persistedPageSizeRef.current` rather than the raw
  // `pageSize` state — see its declaration above.
  useEffect(() => {
    if (!screenCode || !hasHydratedFromPreferences) return;
    updateListPreferences({ filters, sort, pageSize: persistedPageSizeRef.current });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screenCode, hasHydratedFromPreferences, filters, sort, pageSize]);

  const setPage = internalSetPage;

  const setPageSize = useCallback(
    (size: number) => {
      userInteractedRef.current = true;
      const nextPageSize = Number(size) || initialPageSize;
      persistedPageSizeRef.current = nextPageSize;
      internalSetPageSize(nextPageSize);
      internalSetPage(1);
    },
    [initialPageSize],
  );

  const setFilters = useCallback(
    (updater: SetStateAction<TFilters>) => {
      userInteractedRef.current = true;
      internalSetFilters((previous) => {
        const next =
          typeof updater === 'function'
            ? (updater as (prev: TFilters) => TFilters)(previous)
            : updater;
        if (next !== previous) {
          internalSetPage(1);
          internalSetPageSize(initialPageSize);
        }
        return next;
      });
    },
    [initialPageSize],
  ) as Dispatch<SetStateAction<TFilters>>;

  const executeFetch = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    const signal = createSignal();

    let result: PaginatedFetchResult<TRow> | null;
    try {
      result = await fetchFn(
        { page, pageSize, searchText: debouncedSearchText || undefined, sort, filters },
        signal,
      );
    } catch {
      if (!signal.aborted) {
        setRows([]);
        setTotal(0);
        setLoading(false);
      }
      return;
    }

    // Stale request — a newer fetch has already started; discard this result.
    if (signal.aborted) return;

    if (result === null) {
      setRows([]);
      setTotal(0);
      setLoading(false);
      return;
    }

    let { items } = result;
    if (deduplicate) {
      const seen = new Set<number | string>();
      items = items.filter((item) => {
        const id = (item as Record<string, unknown>)['id'] as number | string;
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
      });
    }

    setRows(items);
    setTotal(result.totalRecords);
    setLoading(false);
  }, [enabled, page, pageSize, debouncedSearchText, sort, filters, fetchFn, createSignal,
    deduplicate]);

  useEffect(() => {
    void executeFetch();
  }, [executeFetch]);

  // Page-overflow guard: resets to page 1 when total shrinks below current page.
  useEffect(() => {
    if (total > 0) {
      const totalPages = Math.ceil(total / pageSize);
      if (page > totalPages) internalSetPage(1);
    }
  }, [total, page, pageSize]);

  const onSearch = useCallback(
    (text: string) => {
      setSearchText(text);
    },
    [setSearchText],
  );

  // Uses a ref for orderByMap — stable callback with no stale-closure risk.
  const onSortChange = useCallback(
    (columnKey: string, direction: 'asc' | 'desc') => {
      userInteractedRef.current = true;
      setSort({
        key: orderByMapRef.current[columnKey] ?? columnKey,
        direction: direction.toUpperCase() as 'ASC' | 'DESC',
      });
      internalSetPage(1);
    },
    [],
  );

  const refetch = useCallback(() => {
    void executeFetch();
  }, [executeFetch]);

  return {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    loading,
    onSearch,
    onSortChange,
    sort,
    filters,
    setFilters,
    debouncedSearchText,
    flushSearch,
    refetch,
  };
}
